import { isFieldExpanded, type FormFieldsVisibilityState } from "@/lib/form-field-visibility";

/**
 * Non-maskable ("obligatory") fields per table — always shown in the fiche, so
 * they have no on/off state in the visibility grid. When exports/print derive
 * their columns from the grid, these must always count as visible, even after
 * a "hide all" (`__allMinimized`), where `isFieldExpanded` returns false.
 *
 * Keys match the export/form table keys (see `tabMenuItems`). Values are the
 * raw field keys (same namespace as export config keys and grid field names).
 * Kept in sync with the `nonMaskable` fields in each `edit-*.tsx` form.
 */
export const ALWAYS_VISIBLE_FIELDS_BY_TABLE: Record<string, readonly string[]> = {
  artwork: [
    "oeuvresDescriptionNumeroEnregistrement",
    "oeuvresDescriptionTitre",
  ],
  multipleprint: [
    "estampesMultiplesDescriptionNumeroEnregistrement",
    "estampesMultiplesDescriptionTitre",
  ],
  exhibition: [
    "expositionsDescriptionNumeroExpo",
    "expositionsDescriptionTitre",
  ],
  bibliography: [
    "bibliographieDescriptionNumeroBibliographie",
    "bibliographieDescriptionTitre",
  ],
  auction: ["ventesDescriptionNumeroVente"],
  author: ["artistesAuteursDescriptionNom"],
  collector: ["collectionneursDescriptionNom"],
  audiovisual: [
    "audiovisuelDescriptionNumeroEnregistrement",
    "audiovisuelDescriptionTitre",
  ],
  document: [
    "documentsDescriptionNumeroEnregistrement",
    "documentsDescriptionTitre",
  ],
  writing: [
    "ecritsDescriptionNumeroEnregistrement",
    "ecritsDescriptionTitre",
  ],
  photography: [
    "photosDescriptionNumeroEnregistrement",
    "photosDescriptionTitre",
  ],
};

/** True when `fieldKey` is a non-maskable (always-shown) field of `table`. */
export function isFieldAlwaysVisible(table: string, fieldKey: string): boolean {
  return ALWAYS_VISIBLE_FIELDS_BY_TABLE[table]?.includes(fieldKey) ?? false;
}

/** Whether a field is currently shown in the fiche: non-maskable OR expanded. */
export function isFieldVisibleInFiche(
  table: string,
  fieldKey: string,
  visibility: FormFieldsVisibilityState,
): boolean {
  return (
    isFieldAlwaysVisible(table, fieldKey) ||
    isFieldExpanded(visibility, fieldKey)
  );
}
